import { RSVPLinkSyncStatus } from '@/types/rsvp.types';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import OptionalComponent from '@ui/optional-component/optional-component';
import { useMemo, useState } from 'react';
import useLoadRSVPLinkSync from '@/connection/hooks/use-load-rsvp-link-sync';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import { getRSVPLinkSync } from '@/store/slices/integrations/selectors';
import Modal from '@ui/modal/modal';
import toastTrigger from '@ui/toast/toast-trigger';
import Text from '@ui/text/text/text';
import AdminCard from '@ui/cards/admin/admin-card';
import Container from '@ui/container/container';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import { getWorkingProject } from '@/store/slices/project/selectors';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { approveRSVPEventLink } from '@/store/slices/integrations';
import Button from '@ui/buttons/button/button';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';

import './rsvp-link-card.css';
import { EToastTypes } from '@ui/toast/toast.types';

const RSVPLinkCard = () => {
  useLoadRSVPLinkSync();

  const rsvpLinkSync = useAppSelector(getRSVPLinkSync);
  const workingProject = useAppSelector(getWorkingProject);
  const dispatch = useAppDispatch();

  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const [approvalConfirmationOpen, setApprovalConfirmationOpen] = useState(false);

  function approveLink() {
    // on approval, disable native registration and emails
    if (rsvpLinkSync && workingProject?.event) {
      dispatch(approveRSVPEventLink({ linkGroup: rsvpLinkSync.link_group, eventId: workingProject.event }));
    }
    setApprovalConfirmationOpen(false);
  }

  function getEventLink() {
    const url = location.href;
    return url.substring(0, url.lastIndexOf('/'));
  }

  function copyEventLink() {
    const eventLink = getEventLink();
    navigator.clipboard.writeText(eventLink);
    setLearnMoreOpen(false);
    toastTrigger('Copied to clipboard', {
      type: EToastTypes.info,
      autoClose: 3000,
      description: eventLink,
    });
  }

  function handleCardButton() {
    if (!rsvpLinkSync) {
      copyEventLink();
    } else {
      setApprovalConfirmationOpen(true);
    }
  }

  const buttonText = useMemo(() => {
    switch (rsvpLinkSync?.status) {
      case RSVPLinkSyncStatus.approved: {
        return 'Connection locked';
      }
      case RSVPLinkSyncStatus.requested: {
        return 'Confirm sync';
      }
      default:
        return 'Copy link';
    }
  }, [rsvpLinkSync]);

  return (
    <AdminCard className="settings-integration rsvp-link-card">
      <div className="integration-header flex fd-row jc-space-between">
        <div className="integration-logo">
          <Icon name={ICONS.RSVP_LOGO} size={32} />
        </div>
        <OptionalComponent display={rsvpLinkSync?.status === RSVPLinkSyncStatus.requested}>
          <div className="pending-tag">
            <span>Pending</span>
          </div>
        </OptionalComponent>
      </div>
      <div className="integration-text">
        <span>RSVP Connection</span>
        <p className="S1">Link your RSVP event</p>
        <span>RSVP Integration</span>
      </div>
      <div className="integration-footer flex fd-row jc-space-between">
        <OptionalComponent display={rsvpLinkSync?.status === RSVPLinkSyncStatus.approved}>
          <div className="events-linked-tag">
            <Icon name={ICONS.PADLOCK} size={24} />
            <div className="events-linked-tag-text">
              <span>Connection Locked</span>
            </div>
          </div>
          <TooltipPortal
            tooltipContent={
              <Text color="var(--gray-true-dark-100)">
                This event is linked to a Google Event and can't be unlinked. To learn more, go to&nbsp;
                <a
                  href="https://sites.google.com/corp/google.com/gorsvphelp/learning-hub/integrations"
                  target="_blank"
                  rel="noreferrer"
                >
                  go/rsvp-integrations
                </a>
              </Text>
            }
          >
            <Icon name={ICONS.INFO_OUTLINE} size={16} />
          </TooltipPortal>
        </OptionalComponent>
        <OptionalComponent display={rsvpLinkSync?.status !== RSVPLinkSyncStatus.approved}>
          <Button design="LocalBttnPrim" className="padding-v-8 padding-h-16" onClick={handleCardButton}>
            <Text className="L2">{buttonText}</Text>
          </Button>
          <Button className="L2 padding-v-8 padding-h-16" onClick={() => setLearnMoreOpen(true)}>
            <Text color={getThemeColorCSSVariable()}>Learn more</Text>
          </Button>
        </OptionalComponent>
      </div>

      {/* Learn more modal */}
      <Modal
        open={learnMoreOpen}
        onRequestClose={() => setLearnMoreOpen(false)}
        contentWrapperClassName="padding-v-24 padding-h-40"
        RenderHeaderContent={() => (
          <Container className="margin-b-20">
            <Text tag="h4" className="H4">
              How to link your RSVP Event
            </Text>
          </Container>
        )}
        RenderFooterContent={() => (
          <Container className="flex fd-row jc-flex-end gap-16">
            <button onClick={() => setLearnMoreOpen(false)}>Cancel</button>
            <Button onClick={copyEventLink} design="LocalBttnPrim">
              <Text>Copy link</Text>
            </Button>
          </Container>
        )}
      >
        <div>
          <p className="margin-b-20">
            Copy this link and paste it into the Integrations tab of your RSVP event page to initiate the event link.
            Once initiated, return to this page to confirm the event link.
          </p>
          <InputWithOverlay
            inputProps={{
              value: getEventLink(),
              disabled: true,
              className: 'radius-100 opacity-05',
              type: 'text',
            }}
          />
        </div>
      </Modal>

      {/* Approval confirmation modal */}
      <Modal
        open={approvalConfirmationOpen}
        customSize={{
          height: '250px',
          width: 'auto',
          maxWidth: '500px',
          maxHeight: '100%',
          minHeight: 'auto',
        }}
        onRequestClose={() => setApprovalConfirmationOpen(false)}
        contentWrapperClassName="padding-v-24 padding-h-40"
        RenderHeaderContent={() => (
          <Container>
            <h4>Confirm event sync</h4>
          </Container>
        )}
        RenderFooterContent={() => (
          <Container className="flex-justify-end gap-16">
            <button onClick={() => setApprovalConfirmationOpen(false)}>Cancel</button>
            <Button onClick={approveLink} backgroundColor={getThemeColorCSSVariable()}>
              <Text color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}>
                Confirm sync
              </Text>
            </Button>
          </Container>
        )}
      >
        <div className="rsvp-sync-confirmation-modal">
          <div className="warning-message">
            <div>
              <Icon name={ICONS.EXCLAMATION_MARK} size={20} />
            </div>
            <span>
              Linking this event with RSVP is a permanent action and cannot be undone. Linking will reset any current
              registrations and restrict certain Brandlive features.{' '}
              <a href="https://help.brand.live" target="__blank" rel="noopener">
                Learn More
              </a>
            </span>
          </div>
        </div>
      </Modal>
    </AdminCard>
  );
};

export default RSVPLinkCard;
